import { useState } from 'react'

import type {
  ShipmentDetail,
  ShipmentStatus,
} from '#/shared/api/generated/shipment/models'
import { useGetShipmentApiV1ShipmentsShipmentIdGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { cn } from '#/shared/lib/utils'
import { useDetailSidebar } from '#/shared/ui/detail-sidebar'
import { BookingButton, TrackingButton } from '#/widgets/detail-toolbar'

import {
  SHIPMENT_LINK_KEY,
  SHIPMENT_STAGES,
  ShipmentDetailToolbar,
  ShipmentOwnerDropdown,
  ShipmentRequestsSlot,
  getShipmentLink,
  isPostedToLoadboard,
  sumToolbarCounts,
  useShipmentToolbarCounts,
  shouldShowTracking,
  useShipmentSidebarActions,
  useShipmentDocumentTitle,
  useShipmentToolbarActions,
} from '#/entities/shipment'
import type { ShipmentLinkKey, ShipmentStage } from '#/entities/shipment'
import {
  ShipmentCommentPanel,
  hiddenCommentTabsForStage,
} from '#/widgets/shipment-comment-panel'

import { useShipmentChatUnread } from '../model/use-shipment-chat-unread'
import { HISTORY_TAB, ShipmentHistoryTabs } from './shipment-history-tabs'
import type { HistoryTab } from './shipment-history-tabs'
import { SidebarActivityTab } from './sidebar-activity-tab'
import { SidebarChatTab } from './sidebar-chat-tab'
import { SidebarLoadboardTab } from './sidebar-loadboard-tab'
import { ShipmentSidebarDetails } from './shipment-sidebar-details'

export interface ShipmentSidebarPanelProps {
  shipmentId: string
  stage: ShipmentStage
  /** Raw status from the list row, for optimistic toolbar rendering on open. */
  optimisticStatus?: ShipmentStatus
}

function buildOpenLink(
  shipment: ShipmentDetail | undefined,
  key: ShipmentLinkKey,
) {
  const url = getShipmentLink(shipment?.links, key)
  if (!url) return undefined
  return () => window.open(url, '_blank', 'noopener')
}

export function ShipmentSidebarPanel({
  shipmentId,
  stage,
  optimisticStatus,
}: ShipmentSidebarPanelProps) {
  const { close } = useDetailSidebar()
  const [historyTab, setHistoryTab] = useState<HistoryTab>(HISTORY_TAB.details)
  const [contentScrolled, setContentScrolled] = useState(false)
  const shipmentQuery = useGetShipmentApiV1ShipmentsShipmentIdGet(shipmentId)
  const shipment = shipmentQuery.data?.data
  const { counts } = useShipmentToolbarCounts(shipmentId)
  const chatUnread = useShipmentChatUnread(shipment)
  const actions = useShipmentSidebarActions(stage, shipmentId)
  const openTracking = buildOpenLink(shipment, SHIPMENT_LINK_KEY.tracking)
  const openBooking = buildOpenLink(shipment, SHIPMENT_LINK_KEY.onlineBooking)

  const { primaryAction, archive } = useShipmentToolbarActions({
    stage,
    shipment,
    shipmentId,
    optimisticStatus,
    compact: true,
    onArchived: close,
  })
  useShipmentDocumentTitle(stage, shipment?.code)

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b border-gray-200 px-4 py-2.5">
        <ShipmentDetailToolbar
          compact
          shipment={shipment}
          stage={stage}
          showUser={stage === SHIPMENT_STAGES.LEAD}
          onEdit={actions.onEdit}
          primaryAction={primaryAction}
          requests={
            shouldShowTracking(shipment) ? (
              <TrackingButton onClick={openTracking} />
            ) : (
              <ShipmentRequestsSlot stage={stage} shipment={shipment} />
            )
          }
          archive={archive}
        />
      </div>
      <div className="shrink-0">
        <ShipmentCommentPanel
          shipmentId={shipmentId}
          compact
          hiddenTabs={hiddenCommentTabsForStage(stage)}
        />
        <ShipmentHistoryTabs
          value={historyTab}
          onValueChange={setHistoryTab}
          activityCount={sumToolbarCounts(counts)}
          chatCount={chatUnread}
          showLoadboard={stage === SHIPMENT_STAGES.ORDER}
          loadboardPosted={isPostedToLoadboard(shipment)}
          className={cn(
            'relative z-10 transition-shadow',
            contentScrolled && 'shadow-toolbar',
          )}
        />
      </div>
      <PanelContent
        historyTab={historyTab}
        shipmentId={shipmentId}
        shipment={shipment}
        stage={stage}
        isLoading={shipmentQuery.isLoading}
        onScrolledChange={setContentScrolled}
      />
      {stage !== SHIPMENT_STAGES.LEAD && historyTab !== HISTORY_TAB.chat && (
        <PanelFooter shipment={shipment} onBooking={openBooking} />
      )}
    </div>
  )
}

function PanelFooter({
  shipment,
  onBooking,
}: {
  shipment: ShipmentDetail | undefined
  onBooking?: () => void
}) {
  return (
    <div className="flex h-18 shrink-0 items-center justify-between border-t border-gray-200 px-4">
      <ShipmentOwnerDropdown shipment={shipment} side="top" align="left" />
      {onBooking && <BookingButton onClick={onBooking} />}
    </div>
  )
}

function PanelContent({
  historyTab,
  shipmentId,
  shipment,
  stage,
  isLoading,
  onScrolledChange,
}: {
  historyTab: HistoryTab
  shipmentId: string
  shipment: ShipmentDetail | undefined
  stage: ShipmentStage
  isLoading: boolean
  onScrolledChange: (scrolled: boolean) => void
}) {
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto"
      onScroll={(event) => onScrolledChange(event.currentTarget.scrollTop > 0)}
    >
      {historyTab === HISTORY_TAB.details && (
        <ShipmentSidebarDetails
          shipment={shipment}
          isLoading={isLoading}
          stage={stage}
        />
      )}
      {historyTab === HISTORY_TAB.chat && (
        <SidebarChatTab shipment={shipment} />
      )}
      {historyTab === HISTORY_TAB.activity && (
        <SidebarActivityTab shipmentId={shipmentId} />
      )}
      {historyTab === HISTORY_TAB.loadboard && (
        <SidebarLoadboardTab shipment={shipment} />
      )}
    </div>
  )
}
